"use client";

import { queryOptions, useQuery } from "@tanstack/react-query";
import { QUERIES } from "modules/common/query";
import { CardLGSAvailability } from "modules/cards/model/card";
import { useFetchWrapper } from "modules/common/hooks/useFetchWrapper";
import { API_ROUTES } from "utils/api-route.constants";
import { clientLogger } from "modules/common/logger/client";

interface CardAvailabilityRequest {
  setCode: string;
  number: string;
  cardId?: string;
  isAuthenticated?: boolean;
  hasStores?: boolean;
  shouldFetch: boolean;
}

/**
 * Creates query options for fetching card availability at user's LGS stores
 * Using queryOptions provides better type safety and reusability
 */
export function createCardAvailabilityQueryOptions<T = CardLGSAvailability>(
  req: CardAvailabilityRequest,
  fetchFn: (req: CardAvailabilityRequest) => Promise<CardLGSAvailability>,
  selectFn?: (data: CardLGSAvailability) => T
) {
  return queryOptions({
    queryKey: [QUERIES.CARD_AVAILABILITY, req.setCode, req.number, req.cardId],
    queryFn: () => fetchFn(req),
    enabled:
      !!req.setCode &&
      !!req.number &&
      !!req.isAuthenticated &&
      !!req.hasStores &&
      !!req.shouldFetch,
    select: selectFn,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchInterval: false,
    refetchIntervalInBackground: false,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });
}

export function useCardAvailability<T = CardLGSAvailability>(
  req: CardAvailabilityRequest,
  select?: (data: CardLGSAvailability) => T
) {
  const fetch = useFetchWrapper();

  const fetchCardAvailability = async (
    req: CardAvailabilityRequest
  ): Promise<CardLGSAvailability> => {
    const params = new URLSearchParams({
      setCode: req.setCode,
      number: req.number,
    });
    if (req.cardId) params.set("cardId", req.cardId);
    const q = `?${params.toString()}`;

    try {
      const response = await fetch.get(
        API_ROUTES.TRADE.GET_CARD_AVAILABILITY + q,
        null
      );
      return response as CardLGSAvailability;
    } catch (error) {
      clientLogger.error(
        "useCardAvailability",
        "fetch card availability failed",
        error
      );
      // Return empty arrays on error for graceful degradation
      return {
        available: [],
        wanted: [],
      };
    }
  };

  const query = useQuery(
    createCardAvailabilityQueryOptions(req, fetchCardAvailability, select)
  );

  return {
    availability: query.data,
    query,
  };
}
